import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import type {
  BackupSchedule,
  CertificatesView,
  DeploymentSummary,
  MonitoringView,
  Server,
  Session,
} from '../api/types.ts';
import { Button, Card, CardHeader, EmptyState, Notice, Skeleton } from '../components/ui.tsx';
import { DeploymentList } from './Deployments.tsx';
import { ServerCard } from './Servers.tsx';
import type { Route } from '../components/Shell.tsx';

/**
 * Home.
 *
 * For somebody with nothing yet, this is a single instruction. For somebody
 * with servers, it is what they are and what has been installed on them.
 * Nothing else: a dashboard full of charts is for us, not for the customer.
 */
export function Dashboard({
  session,
  onNavigate,
}: {
  session: Session;
  onNavigate: (route: Route) => void;
}): ReactNode {
  const { t } = useI18n();
  const [servers, setServers] = useState<readonly Server[] | undefined>();
  const [deployments, setDeployments] = useState<readonly DeploymentSummary[] | undefined>();
  const [attention, setAttention] = useState<Attention | undefined>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void (async () => {
      // Not `catch(() => [])`. That renders "you have not added a server yet"
      // and an "add my first server" button to a customer who has ten and
      // whose network dropped — a failure shown as a fact, on the first
      // screen they see, about the thing they would most panic about.
      const [serverList, deploymentList] = await Promise.all([
        api.servers().catch(() => undefined),
        api.deployments().catch(() => undefined),
      ]);
      if (!serverList) {
        setFailed(true);
        return;
      }
      setServers(serverList);
      setDeployments(deploymentList ?? []);

      // Second, and separately. The dashboard is usable without it, so a
      // monitoring query that fails must not hold up the page or empty it —
      // it just means this part has nothing to say yet.
      const [monitoring, backups, certificates] = await Promise.all([
        api.monitoring().catch(() => undefined),
        api.backups().catch(() => undefined),
        api.certificates().catch(() => undefined),
      ]);
      if (monitoring) setAttention(summarise(monitoring, backups?.schedules, certificates));
    })();
  }, []);

  if (failed) {
    return (
      <Card>
        <EmptyState
          title={t('error.network')}
          action={
            <Button variant="secondary" onClick={() => window.location.reload()}>
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  if (!servers || !deployments) {
    return (
      <Card>
        <Skeleton lines={5} />
      </Card>
    );
  }

  const active = servers.filter((server) => !server.authorizationRevokedAt);

  return (
    <>
      <header className="page-header">
        <div>
          <h1>{t('dashboard.greeting', { name: session.user.displayName })}</h1>
          <p className="muted">{t('app.tagline')}</p>
        </div>
        {active.length > 0 ? (
          <Button size="lg" onClick={() => onNavigate({ name: 'newDeployment' })}>
            {t('dashboard.startInstall')}
          </Button>
        ) : null}
      </header>

      {active.length === 0 ? (
        <Card>
          <EmptyState
            title={t('dashboard.noServers')}
            description={t('dashboard.noServersHelp')}
            action={
              <Button size="lg" onClick={() => onNavigate({ name: 'addServer' })}>
                {t('dashboard.addFirstServer')}
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          {attention ? <AttentionSummary attention={attention} onNavigate={onNavigate} /> : null}

          <section>
            <CardHeader
              title={t('servers.title')}
              description={t('dashboard.serversCount', { count: active.length })}
              actions={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onNavigate({ name: 'servers' })}
                >
                  {t('deployments.view')}
                </Button>
              }
            />
            <div className="server-list">
              {active.slice(0, 2).map((server) => (
                <ServerCard key={server.id} server={server} />
              ))}
            </div>
          </section>

          <section>
            <CardHeader title={t('dashboard.recentDeployments')} />
            {deployments.length === 0 ? (
              <Card>
                <p className="muted">{t('dashboard.nothingYet')}</p>
              </Card>
            ) : (
              <DeploymentList
                deployments={deployments.slice(0, 5)}
                onOpen={(id) => onNavigate({ name: 'deployment', id })}
              />
            )}
          </section>
        </>
      )}
    </>
  );
}

/**
 * What, if anything, the customer should do next.
 *
 * Three questions, in the order they matter: is anything broken, is anything
 * unprotected, and is anything going to embarrass them in a browser. Each one
 * links to the screen that answers it properly — this is a summary, and a
 * summary that tries to be the screen is neither.
 *
 * "Everything is working" is shown too, and is the reason this exists. A
 * dashboard that only speaks up when something is wrong leaves a customer
 * unable to tell "all well" from "not looked".
 */
interface Attention {
  readonly unhealthy: number;
  readonly unprotectedPanels: number;
  readonly bootstrapCertificates: number;
}

function summarise(
  monitoring: MonitoringView,
  schedules: readonly BackupSchedule[] | undefined,
  certificates: CertificatesView | undefined,
): Attention {
  const scheduled = new Set((schedules ?? []).map((schedule) => schedule.installationId));
  return {
    unhealthy: [...monitoring.panels, ...monitoring.tunnels].filter(
      (resource) => resource.status === 'unhealthy' || resource.status === 'degraded',
    ).length,
    unprotectedPanels: monitoring.panels.filter((panel) => !scheduled.has(panel.id)).length,
    bootstrapCertificates: (certificates?.panels ?? []).filter(
      (panel) => panel.kind === 'internal_bootstrap',
    ).length,
  };
}

function AttentionSummary({
  attention,
  onNavigate,
}: {
  attention: Attention;
  onNavigate: (route: Route) => void;
}): ReactNode {
  const { t } = useI18n();

  if (
    attention.unhealthy === 0 &&
    attention.unprotectedPanels === 0 &&
    attention.bootstrapCertificates === 0
  ) {
    return <Notice tone="success">{t('dashboard.everythingWorking')}</Notice>;
  }

  return (
    <>
      {attention.unhealthy > 0 ? (
        <Notice
          tone="warning"
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onNavigate({ name: 'monitoring' })}
            >
              {t('dashboard.openMonitoring')}
            </Button>
          }
        >
          {t('dashboard.needsAttention', { count: attention.unhealthy })}
        </Notice>
      ) : null}

      {attention.unprotectedPanels > 0 ? (
        <Notice
          tone="info"
          actions={
            <Button size="sm" variant="secondary" onClick={() => onNavigate({ name: 'backups' })}>
              {t('dashboard.openBackups')}
            </Button>
          }
        >
          {t('dashboard.backupsOff', { count: attention.unprotectedPanels })}
        </Notice>
      ) : null}

      {attention.bootstrapCertificates > 0 ? (
        <Notice
          tone="info"
          actions={
            <Button size="sm" variant="secondary" onClick={() => onNavigate({ name: 'settings' })}>
              {t('dashboard.openSettings')}
            </Button>
          }
        >
          {t('dashboard.bootstrapCertificate', { count: attention.bootstrapCertificates })}
        </Notice>
      ) : null}
    </>
  );
}
